/**
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-router-dom components
import { useLocation } from "react-router-dom";

// @mui material components
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function DefaultNavbarDropdown({
  name,
  icon,
  children,
  collapseStatus,
  light,
  route,
  collapse,
  ...rest
}) {
  const { pathname } = useLocation(); // 현재 경로 가져오기
  const isActive = route && pathname === route; // 현재 메뉴가 활성화 상태인지 확인
  const hoverColor = "rgba(104, 52, 155, 0.3)"; // 호버 배경색 (불투명도 80%)
  const activeColor = "rgba(104, 52, 155, 0.8)"; // 활성화 배경색 (불투명도 30%)

  return (
    <>
      <MKBox
        {...rest}
        mx={1}
        p={1}
        display="flex"
        alignItems="baseline"
        color={light ? "white" : "dark"}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: isActive ? activeColor : "transparent", // 활성화 상태 배경색
          borderRadius: isActive ? "8px" : "0",
          "&:hover": {
            backgroundColor: hoverColor, // 호버 배경색
          },
        }}
      >
        <MKTypography
          variant="body2"
          lineHeight={1}
          color={isActive ? "white" : "inherit"} // 활성화 상태에서는 약간 어두운 흰색
          sx={{ alignSelf: "center", "& *": { verticalAlign: "middle" } }}
        >
          {icon}
        </MKTypography>
        <MKTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={isActive ? "white" : light ? "white" : "dark"} // 활성화된 메뉴의 텍스트 색상은 흰색
          sx={{ fontWeight: "100%", ml: 1, mr: 0.25 }}
        >
          {name}
        </MKTypography>
        <MKTypography
          variant="body2"
          color={isActive ? "rgba(255, 255, 255, 0.9)" : light ? "white" : "dark"} // 활성화 상태는 흰색
          ml="auto"
        >
          <Icon
            sx={{
              fontWeight: "normal",
              verticalAlign: "middle",
              color: isActive ? "rgba(255, 255, 255, 0.9)" : light ? "white" : "dark", // 아이콘 색상 설정
            }}
          >
            {collapse && "keyboard_arrow_down"}
          </Icon>
        </MKTypography>
      </MKBox>
      {children && (
        <Collapse in={Boolean(collapseStatus)} timeout={400} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Setting default values for the props of DefaultNavbarDropdown
DefaultNavbarDropdown.defaultProps = {
  children: false,
  collapseStatus: false,
  light: false,
  href: "",
  route: "",
  status: true,
};

// Typechecking props for the DefaultNavbarDropdown
DefaultNavbarDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  children: PropTypes.node,
  collapseStatus: PropTypes.bool,
  light: PropTypes.bool,
  href: PropTypes.string,
  route: PropTypes.string,
  collapse: PropTypes.bool.isRequired,
  status: PropTypes.bool,
};

export default DefaultNavbarDropdown;
